import React from 'react';
import { trace } from '../lib/trace';
import { BUILD } from '../lib/supabase';
import StopCard from './StopCard';

/*
  What OnHand shows when it breaks.

  **Written 7 September 2026, and it should have existed from the first day.**
  The app had no boundary at all, which means any component that threw while
  drawing took the entire app down with it: React unmounts the whole tree and
  says why in a console nobody has open. A person sees the screen vanish and has
  no idea what happened or what to do.

  Rule 35 already settled what a crash looks like: no shell, because the crash
  may have taken the shell with it, and a card that says what to do rather than
  what went wrong.

  **Rebuilt onto the approved stop card, 10 September 2026, job 25.** It was
  hand-built on 7 September, three days before the shape it should have used
  reached the app, so it had its own corners, its own shadow, its own button and
  its own wording, none of which matched the drawing the owner signed off on 5
  September. **This is the same fault the idle warning had**, and both were
  invisible for the same reason: nobody ever sees these screens, so nobody
  notices that they do not match.

  The wording is now Group H's, word for word, and it is the honest sentence
  rather than the comfortable one: **anything you had saved is safe, anything
  you were part way through is not.** "Sorry, please try again" would leave a rep
  wondering whether the shelf they had just counted counted.

  **The one departure from the drawing is deliberate and it is kept**: the card
  also shows the build stamp and the actual message, small, at the bottom. Rule
  35 says never print what went wrong inside OnHand, and that is right for
  somebody who can simply try again. It is wrong for somebody stuck on one fault
  for an evening with nothing to tell anybody, which is exactly the evening this
  file was written in. It is written down here rather than left as a difference
  somebody finds later and 'fixes'.
*/
export default class Boundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { err: null };
  }

  static getDerivedStateFromError(err) {
    return { err };
  }

  componentDidCatch(err, info) {
    trace('crashed', {
      message: String(err?.message || err).slice(0, 300),
      where: String(info?.componentStack || '').split('\n').slice(1, 5).join(' | '),
    });
  }

  render() {
    if (!this.state.err) return this.props.children;
    /* No shell, rule 35: the crash may have taken the shell with it, so a card
       that needs the bar to draw itself would fail exactly when it is needed.
       `login-page` is the app's own bare canvas, which is what the drawing
       stands on and what the opening screen and the sign-in screen both use. */
    return (
      <div className="login-page" style={{ display: 'block' }}>
        <StopCard
          mark="stopped"
          tone="red"
          title="OnHand has stopped"
          says="Something inside OnHand failed. Anything you had saved is safe; anything you were part way through is not."
          act={{ label: 'Reload OnHand', onClick: () => window.location.reload() }}
        />
        {/* The departure, and the reason for it is in the note at the top of
            this file. Below the card rather than inside it, because it is for
            whoever looks after OnHand and not for the person who was working. */}
        <pre style={{
          maxWidth: 460, margin: '0 auto', fontSize: 10.5, lineHeight: 1.5,
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          background: 'var(--text-strong)', color: 'var(--bg)', borderRadius: 'var(--oh-radius-control)', padding: 11,
        }}>
          {'Build ' + BUILD + '\n' + String(this.state.err?.message || this.state.err)}
        </pre>
      </div>
    );
  }
}
